/*
 * alatunniste-majakka.js — keeps the lighthouse being drawn WHILE SOMEBODY IS
 * LOOKING AT IT, and does nothing else.
 *
 * ⚠️ REWRITTEN 2026-09-22 BECAUSE THE FIRST VERSION SNAPPED. It set one
 * attribute the first time an IntersectionObserver said the drawing was 35%
 * on screen, then disconnected. That is correct only if the reader is still
 * watching for the next 11 seconds, which is how long the choreography runs
 * (the beams start at 9.8s — see the keyframes in
 * sections/alatunniste.liquid). It very often was not, and there were two
 * independent ways to lose the whole animation:
 *
 *   A. OCCLUSION, and this one was guaranteed rather than unlucky. When the
 *      footer reveal engages (body.alatunniste-paljastus, i.e. a viewport
 *      about as tall as the footer) the footer becomes `position: fixed` and
 *      the page is painted OVER it. IntersectionObserver does not model
 *      occlusion at all — a fixed element is "intersecting the viewport" from
 *      the first frame, however much content is covering it. So the trigger
 *      fired at load, the whole sequence ran out behind the page, and the reader
 *      scrolled down to a finished drawing every single time.
 *
 *   B. THE ELEVEN-SECOND WINDOW. Even in the ordinary in-flow footer, a
 *      one-shot trigger and a very long payload is a bet that the reader does
 *      not scroll on, switch tab, or arrive via a reload that restored the
 *      scroll position near the bottom. Background tabs throttle rAF but CSS
 *      animations still advance, so opening the site in a new tab and coming
 *      back a minute later also produced a finished lighthouse.
 *
 * THE FIX IS NOT A LONGER THRESHOLD, IT IS A PAUSE. The drawing still starts
 * once and is never restarted — it is a full stop, not a loop — but its
 * animations are paused whenever it is not actually visible and resumed when
 * it is. The eleven seconds are therefore always spent in front of somebody.
 * That makes the trigger point almost uncritical, which is the whole point:
 * the previous design needed the trigger to be exactly right and had no way
 * to recover when it was not.
 *
 * Still true, and still the constraints this file works under:
 *   - Every frame is a CSS @keyframes inside `prefers-reduced-motion:
 *     no-preference`, so no-motion is the DEFAULT rather than a fallback.
 *   - `data-majakka-js` is the handshake: the CSS hides the drawing behind the
 *     wipe ONLY once this file is known to be running. Blocked, slow or
 *     broken, the lighthouse simply stands there finished.
 *   - No rAF loop, and no permanent listeners: everything is torn down the
 *     moment the drawing finishes. The scroll listeners are passive and go on
 *     the REAL scrollers (see `vierittajat`), because at ≥990px this theme
 *     scrolls .page-wrapper rather than the document and a scroll event fired
 *     at an element does not bubble to window.
 *   - §4.9's `animation-timeline: view()` still cannot do this. The lighthouse
 *     is the last element on the page, so a scroll-linked reveal has no scroll
 *     left to finish with; and under the reveal it is fixed, which a view
 *     timeline cannot follow either.
 */

const doc = globalThis.document;
const REDUCED = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');

/* How much of the drawing has to be genuinely uncovered before it runs. */
const OSUUS = 0.35;

/* Is the drawing actually being seen right now?
 *
 * Deliberately NOT `entry.intersectionRatio`. Two things that ratio gets
 * wrong here, and both of them bit:
 *
 *   - It ignores the page painted on top of a fixed footer (failure A above),
 *     so the uncovered band is measured from the content's bottom edge
 *     instead whenever the reveal is engaged.
 *   - 35% OF THE ELEMENT can be taller than the viewport. On a short window a
 *     437px drawing is fine, but the ratio is capped at viewportHeight /
 *     elementHeight, so a drawing taller than the window could never reach the
 *     threshold and would stay hidden behind its own wipe forever. The test is
 *     35% of whichever is smaller. */
function nakyvissa(el) {
  if (doc.visibilityState === 'hidden') return false;

  const r = el.getBoundingClientRect();
  const vh = globalThis.innerHeight || 0;
  if (!r.height || !vh) return false;

  let ylin = Math.max(r.top, 0);
  const alin = Math.min(r.bottom, vh);

  if (doc.body.classList.contains('alatunniste-paljastus')) {
    const sisalto = doc.querySelector('.content-for-layout');
    if (sisalto) ylin = Math.max(ylin, sisalto.getBoundingClientRect().bottom);
  }

  return Math.max(0, alin - ylin) >= Math.min(r.height, vh) * OSUUS;
}

/* The part animations live on the children, not on [data-majakka]. */
const animaatiot = (el) => el.getAnimations?.({ subtree: true }) ?? [];

/* Every element that can scroll this one out of view.
 *
 * ⚠️ `window` IS NOT ENOUGH ON THIS THEME. At ≥990px Horizon scrolls
 * `.page-wrapper`, not the document (html and body are height:100dvh;
 * overflow:hidden — assets/base.css), and a scroll event fired at an element
 * does not bubble. So the listener has to go on the scroller itself, and the
 * scroller changes at a breakpoint — hence walking up from the drawing rather
 * than hardcoding a selector. `window` stays in the list for the pages and
 * widths where the document is the scroller. */
function vierittajat(el) {
  const lista = [globalThis];
  for (let n = el.parentElement; n; n = n.parentElement) {
    const yli = getComputedStyle(n).overflowY;
    if (yli === 'auto' || yli === 'scroll') lista.push(n);
  }
  return lista;
}

function mount(scope) {
  for (const el of scope.querySelectorAll?.('[data-majakka]') ?? []) {
    if (el.dataset.majakkaJs !== undefined || REDUCED?.matches) continue;
    el.dataset.majakkaJs = '';

    let kaynnissa = false;
    let valmis = false;

    let kuuntelijat = [];

    const irrota = () => {
      valmis = true;
      observer.disconnect();
      for (const t of kuuntelijat) t.removeEventListener('scroll', paivita);
      kuuntelijat = [];
      globalThis.removeEventListener('resize', paivita);
      doc.removeEventListener('visibilitychange', paivita);
    };

    /* Tear down as soon as the last stroke lands, so a page whose footer has
       been read carries no listeners at all. `finished` rejects if an
       animation is cancelled, hence allSettled. */
    const odotaLoppua = () => {
      const anim = animaatiot(el);
      if (!anim.length) return;
      Promise.allSettled(anim.map((a) => a.finished)).then(() => {
        /* Only really finished if none are still paused mid-flight. */
        if (animaatiot(el).every((a) => a.playState === 'finished')) irrota();
      });
    };

    function paivita() {
      if (valmis) return;
      const nyt = nakyvissa(el);
      if (nyt === kaynnissa) return;
      kaynnissa = nyt;

      if (!nyt) {
        for (const a of animaatiot(el)) a.pause();
        return;
      }

      if (el.dataset.majakkaPiirtyy === undefined) {
        el.dataset.majakkaPiirtyy = '';
        /* The animations do not exist until the style change has been
           applied, so the finish watcher waits one frame for them. */
        requestAnimationFrame(odotaLoppua);
        return;
      }
      for (const a of animaatiot(el)) a.play();
    }

    /* The observer is the cheap signal for the ordinary in-flow footer. Under
       the reveal it is useless on its own (it reports "intersecting" forever),
       which is what the scroll listener is for — passive, and removed again
       the moment the drawing completes. */
    const observer = new IntersectionObserver(paivita, {
      threshold: [0, OSUUS, 1],
    });
    observer.observe(el);

    kuuntelijat = vierittajat(el);
    for (const t of kuuntelijat) t.addEventListener('scroll', paivita, { passive: true });
    globalThis.addEventListener('resize', paivita, { passive: true });
    doc.addEventListener('visibilitychange', paivita);

    paivita();
  }
}

doc.addEventListener('shopify:section:load', (event) => mount(event.target));
mount(doc);
